import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { InstallButton } from '../components/InstallButton';
import {
  CalendarDays,
  Users,
  Clock,
  ShieldCheck,
  Smartphone,
  Download,
  LogIn,
  Zap,
  CheckCircle2,
  MonitorSmartphone,
  Apple,
  Globe,
  Stethoscope,
  HeartHandshake,
  Siren,
  Shield,
  Wrench,
} from 'lucide-react';

const AUDIENCES = [
  { icon: Stethoscope, label: 'Medic providers' },
  { icon: HeartHandshake, label: 'Home & community care' },
  { icon: Siren, label: 'Event medical cover' },
  { icon: Shield, label: 'Security & stewarding' },
  { icon: Wrench, label: 'Mobile engineering' },
];

const FEATURES = [
  { icon: CalendarDays, title: 'Workforce Scheduling', desc: 'Drag-and-drop calendar, multi-day shifts, auto-conflict detection.' },
  { icon: Users, title: 'Employee Management', desc: 'Staff profiles, roles, availability and leave balances in one place.' },
  { icon: Clock, title: 'Time Tracking', desc: 'GPS check-in / check-out with live map and timestamped payable hours.' },
  { icon: ShieldCheck, title: 'Compliance Monitoring', desc: 'Document expiry alerts, upload approval and audit-ready evidence.' },
  { icon: Smartphone, title: 'Mobile Access', desc: 'Field portal built for phones — upload docs, book shifts, request leave on the go.' },
  { icon: MonitorSmartphone, title: 'Installable App', desc: 'Install Elara on Windows, Mac, Android and iPhone for a native-feel experience.' },
];

const PLATFORMS = [
  { icon: Globe, label: 'Windows', hint: 'Chrome or Edge → Install' },
  { icon: Apple, label: 'Mac', hint: 'Chrome or Edge → Install' },
  { icon: Smartphone, label: 'Android', hint: 'Chrome → Install app' },
  { icon: Apple, label: 'iPhone', hint: 'Safari → Add to Home Screen' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F2F2F2]">
      {/* Hero animation keyframes — scoped to this page */}
      <style>{`
        @keyframes elara-fade-up {
          0% { opacity: 0; transform: translate3d(0, 16px, 0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0); }
        }
        @keyframes elara-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes elara-float-in {
          0% { opacity: 0; transform: translate3d(0, 24px, 0) scale(0.98); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
        }
        .elara-fade-up { opacity: 0; animation: elara-fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .elara-fade-in { opacity: 0; animation: elara-fade-in 0.8s ease-out forwards; }
        .elara-float-in { opacity: 0; animation: elara-float-in 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .elara-fade-up, .elara-fade-in, .elara-float-in { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur bg-[#1A1A1A]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="landing-logo">
            <div className="w-9 h-9 rounded-lg bg-[#1ABC9C] flex items-center justify-center font-bold text-[#1A1A1A]">E</div>
            <span className="font-semibold tracking-tight">Elara WFM</span>
          </Link>
          <nav className="flex items-center gap-2 md:gap-3">
            <InstallButton
              variant="ghost"
              size="sm"
              className="hidden md:inline-flex text-[#C7C7C7] hover:text-[#F2F2F2] hover:bg-white/5"
              label="Install app"
              testId="landing-nav-install-btn"
            />
            <Button asChild variant="ghost" size="sm" className="text-[#C7C7C7] hover:text-[#F2F2F2] hover:bg-white/5">
              <Link to="/login" data-testid="landing-nav-signin-link">
                <LogIn className="w-4 h-4 mr-1.5" />
                Sign in
              </Link>
            </Button>
            <Button asChild size="sm" className="bg-[#1ABC9C] hover:bg-[#17A68A] text-[#1A1A1A] font-semibold">
              <Link to="/register" data-testid="landing-nav-trial-link">Start free trial</Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-12 lg:gap-16 items-center">
          <div className="max-w-2xl">
            <div className="elara-fade-up inline-flex items-center gap-2 bg-[#1ABC9C]/10 border border-[#1ABC9C]/30 rounded-full px-3 py-1 text-xs font-medium text-[#1ABC9C] mb-6" style={{ animationDelay: '0ms' }} data-testid="landing-trial-badge">
              <Zap className="w-3.5 h-3.5" />
              7-day full-access trial — every feature, real environment
            </div>
            <h1 className="elara-fade-up text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" style={{ animationDelay: '80ms' }}>
              Workforce management<br />
              <span className="text-[#1ABC9C]">built for teams in the field.</span>
            </h1>
            <p className="elara-fade-up mt-6 text-base md:text-lg text-[#C7C7C7] max-w-2xl" style={{ animationDelay: '180ms' }}>
              From medics and social-care workers to any mobile, on-the-ground team — Elara brings scheduling, compliance, GPS check-in, expenses and reporting into one installable app. Run your rota from the desktop or your phone — no limited demos, no feature locks.
            </p>
            <div className="elara-fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: '280ms' }}>
              <Button asChild size="lg" className="bg-[#1ABC9C] hover:bg-[#17A68A] text-[#1A1A1A] font-semibold px-6">
                <Link to="/register" data-testid="landing-hero-trial-link">Start free trial</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/15 text-[#F2F2F2] hover:bg-white/5 px-6">
                <Link to="/login" data-testid="landing-hero-signin-link">
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in
                </Link>
              </Button>
              <InstallButton
                variant="outline"
                size="lg"
                className="border-[#1ABC9C]/40 text-[#1ABC9C] hover:bg-[#1ABC9C]/10 px-6"
                label="Install Elara WFM"
                testId="landing-hero-install-btn"
              />
            </div>
            <ul className="elara-fade-up mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#A1A1AA]" style={{ animationDelay: '380ms' }}>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C]" /> No credit card required</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C]" /> Full feature access</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C]" /> Installable during trial</li>
            </ul>
          </div>

          {/* Hero visual — real product screenshot */}
          <div className="elara-float-in relative hidden lg:block" style={{ animationDelay: '220ms' }} data-testid="landing-hero-visual">
            {/* Soft teal glow */}
            <div
              aria-hidden="true"
              className="absolute -inset-6 bg-[radial-gradient(ellipse_at_center,rgba(26,188,156,0.25),transparent_60%)] blur-2xl pointer-events-none"
            />
            {/* Browser-chrome frame */}
            <div className="relative rounded-xl border border-white/10 bg-[#111] shadow-2xl shadow-black/50 overflow-hidden ring-1 ring-white/5">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-[#1F1F1F] border-b border-white/5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <span className="ml-3 text-[11px] text-[#71717A] font-mono truncate">elarasystems.co.uk/dashboard</span>
              </div>
              <img
                src="/dashboard-preview.jpg"
                alt="Elara dashboard preview showing staff compliance, 7-day shift coverage and expense summary"
                className="block w-full h-auto"
                loading="eager"
                decoding="async"
                data-testid="landing-hero-image"
              />
            </div>
            {/* Floating badge */}
            <div className="elara-fade-in absolute -left-4 top-10 hidden xl:flex items-center gap-2 rounded-full bg-[#1A1A1A] border border-[#1ABC9C]/40 px-3 py-1.5 text-xs text-[#1ABC9C] shadow-lg" style={{ animationDelay: '650ms' }}>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Live compliance tracking
            </div>
            <div className="elara-fade-in absolute -right-3 -bottom-4 hidden xl:flex items-center gap-2 rounded-full bg-[#1A1A1A] border border-white/10 px-3 py-1.5 text-xs text-[#F2F2F2] shadow-lg" style={{ animationDelay: '800ms' }}>
              <CalendarDays className="w-3.5 h-3.5 text-[#1ABC9C]" />
              Drag-and-drop rota
            </div>
          </div>
        </div>
      </section>

      {/* "Who it's for" strip — thin audience band between hero and
          features so visitors in care, event medical, security,
          home-care and mobile engineering instantly see themselves. */}
      <section
        className="border-y border-white/5 bg-[#1A1A1A]/60"
        data-testid="landing-audiences"
        aria-label="Who Elara is for"
      >
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <span
              className="shrink-0 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] font-mono"
              data-testid="landing-audiences-label"
            >
              Who it's for
            </span>
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:gap-x-7">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                return (
                  <li
                    key={a.label}
                    className="flex items-center gap-2 text-sm text-[#C7C7C7]"
                    data-testid={`landing-audience-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4 text-[#1ABC9C]" />
                    <span>{a.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Trust / Feature callouts */}
      <section className="bg-[#222]/50 border-y border-white/5" data-testid="landing-features">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="max-w-xl mb-10">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Everything you need to run the rota</h2>
            <p className="mt-3 text-[#C7C7C7]">Replace spreadsheets, WhatsApp threads and three different trackers with one tool.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-xl bg-[#1A1A1A] border border-white/5 p-5 hover:border-[#1ABC9C]/30 transition-colors" data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#1ABC9C]/10 border border-[#1ABC9C]/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#1ABC9C]" />
                  </div>
                  <h3 className="font-semibold">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-[#A1A1AA] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Install section */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20" data-testid="landing-install-section">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-medium text-[#1ABC9C] mb-3">
              <Download className="w-3.5 h-3.5" />
              Installable app
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Install Elara. Launch from your home screen.</h2>
            <p className="mt-4 text-[#C7C7C7]">
              Elara is a Progressive Web App — install it on Windows, Mac, Android or iPhone for a full-screen, native-feel experience. No browser tabs. No app store. One click.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#C7C7C7]">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C] mt-0.5" /> Launches instantly from desktop or home screen</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C] mt-0.5" /> Receives automatic update notifications</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C] mt-0.5" /> Shows an offline screen instead of a dead tab</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#1ABC9C] mt-0.5" /> Push notifications for shifts, requests and compliance</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <InstallButton
                variant="default"
                className="bg-[#1ABC9C] hover:bg-[#17A68A] text-[#1A1A1A] font-semibold"
                label="Install Elara WFM"
                testId="landing-install-section-btn"
              />
              <Button asChild variant="outline" className="border-white/15 text-[#F2F2F2] hover:bg-white/5">
                <Link to="/register" data-testid="landing-install-section-trial-link">Start free trial</Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} className="rounded-xl bg-[#2B2B2B] border border-white/5 p-5 text-center" data-testid={`landing-platform-${p.label.toLowerCase()}`}>
                  <Icon className="w-6 h-6 mx-auto text-[#1ABC9C] mb-2" />
                  <div className="font-semibold">{p.label}</div>
                  <div className="text-xs text-[#A1A1AA] mt-1">{p.hint}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold">Already using Elara?</h2>
            <p className="mt-1 text-[#C7C7C7] text-sm">Head straight to your account. Forgot your password? There's a reset link on the sign-in page.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="bg-[#1ABC9C] hover:bg-[#17A68A] text-[#1A1A1A] font-semibold">
              <Link to="/login" data-testid="landing-footer-signin-link">
                <LogIn className="w-4 h-4 mr-2" />
                Sign in
              </Link>
            </Button>
            <Button asChild variant="ghost" className="text-[#C7C7C7] hover:text-[#F2F2F2] hover:bg-white/5">
              <Link to="/forgot-password" data-testid="landing-footer-forgot-link">Forgot password?</Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 text-xs text-[#71717A]">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 flex flex-wrap gap-4 items-center justify-between">
          <span>© {new Date().getFullYear()} Elara Workforce Management</span>
          <span>Built for medics, care teams and every mobile workforce</span>
        </div>
      </footer>
    </div>
  );
}
